import { useTranslation } from "react-i18next"
import { Skeleton, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import type { DrilldownCreativeDrawerModel, DrawerNarrativeField } from "../../lib/drilldown-drawer-model"
import { DRAWER_NARRATIVE_FIELDS } from "../../lib/drilldown-drawer-model"
import type { DrawerNarrativeContent } from "../../lib/drawer-narrative-content"
import {
  DRAWER_CONCEPT_PILL_COLORS,
  DRAWER_SEQUENCE_PILL_COLORS,
  drawerPillColorAt,
} from "../../lib/drawer-pill-colors"
import { parseSequenceStartSeconds } from "../../lib/drawer-sequence-time"
import { avatarColor } from "@/lib/avatar-color"
import { DrilldownCreativeMetricsGrid } from "./DrilldownCreativeMetricsGrid"

interface DrilldownCreativeInfoTabProps {
  model: DrilldownCreativeDrawerModel
  narrative: DrawerNarrativeContent
  rank: number | null
  isVideo: boolean
  isHashDataLoading?: boolean
  onSequenceSeek?: (seconds: number) => void
}

const TEXT_NARRATIVE_FIELDS = [
  "topics",
  "presenterAge",
  "targetAudience",
  "angle",
  "painPoint",
  "motivation",
  "persona",
] as const satisfies ReadonlyArray<
  keyof Pick<
    DrawerNarrativeContent,
    "topics" | "presenterAge" | "targetAudience" | "angle" | "painPoint" | "motivation" | "persona"
  >
>

const NARRATIVE_LABEL_KEYS: Record<DrawerNarrativeField, string> = {
  topics: "drilldown.drawer.topics",
  presenterAge: "drilldown.drawer.presenterAge",
  targetAudience: "drilldown.drawer.targetAudience",
  angle: "drilldown.drawer.angle",
  painPoint: "drilldown.drawer.painPoint",
  motivation: "drilldown.drawer.motivation",
  persona: "drilldown.drawer.persona",
  concepts: "drilldown.drawer.concepts",
  sequence: "drilldown.drawer.sequence",
  verbalHooks: "drilldown.drawer.verbalHooks",
  textOverlay: "drilldown.drawer.textOverlay",
}

const NARRATIVE_DEFAULTS: Record<DrawerNarrativeField, string> = {
  topics: "Topics",
  presenterAge: "Presenter Age",
  targetAudience: "Target Audience",
  angle: "Angle",
  painPoint: "Pain Point",
  motivation: "Motivation",
  persona: "Persona",
  concepts: "Concepts",
  sequence: "Sequence",
  verbalHooks: "Verbal Hooks",
  textOverlay: "Text Overlay",
}

function NarrativeTextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-3">
      <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">{label}</p>
      <p className="text-[13px] leading-snug text-foreground">{value}</p>
    </div>
  )
}

function ConceptPills({ concepts }: { concepts: string[] }) {
  const { t } = useTranslation()
  const emptyValue = t("drilldown.drawer.emptyValue", "—")

  if (concepts.length === 0) {
    return <p className="pt-2 text-[13px] text-foreground">{emptyValue}</p>
  }

  return (
    <div className="flex flex-wrap gap-1.5 pt-2">
      {concepts.map((concept, index) => {
        const colors = drawerPillColorAt(DRAWER_CONCEPT_PILL_COLORS, index)
        return (
          <span
            key={`${concept}-${index}`}
            className="inline-flex rounded-lg px-2 py-0.5 text-xs leading-[18px]"
            style={{ backgroundColor: colors.backgroundColor, color: colors.color }}
          >
            {concept}
          </span>
        )
      })}
    </div>
  )
}

function SequencePills({
  sequence,
  isVideo,
  onSequenceSeek,
}: {
  sequence: DrawerNarrativeContent["sequence"]
  isVideo: boolean
  onSequenceSeek?: (seconds: number) => void
}) {
  const { t } = useTranslation()
  const emptyValue = t("drilldown.drawer.emptyValue", "—")

  if (sequence.length === 0) {
    return <p className="pt-2 text-[13px] text-foreground">{emptyValue}</p>
  }

  return (
    <div className="flex flex-wrap gap-1.5 pt-2">
      {sequence.map((item, index) => {
        const colors = drawerPillColorAt(DRAWER_SEQUENCE_PILL_COLORS, index)
        const startSeconds = item.timeRange ? parseSequenceStartSeconds(item.timeRange) : null
        const canSeek = isVideo && startSeconds !== null && onSequenceSeek

        const pill = (
          <span
            className={
              canSeek
                ? "inline-flex cursor-pointer rounded-lg px-2 py-0.5 text-xs leading-[18px] transition-opacity hover:opacity-80"
                : "inline-flex rounded-lg px-2 py-0.5 text-xs leading-[18px]"
            }
            style={{ backgroundColor: colors.backgroundColor, color: colors.color }}
            onClick={
              canSeek
                ? () => {
                    onSequenceSeek(startSeconds)
                  }
                : undefined
            }
            onKeyDown={
              canSeek
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      onSequenceSeek(startSeconds)
                    }
                  }
                : undefined
            }
            role={canSeek ? "button" : undefined}
            tabIndex={canSeek ? 0 : undefined}
          >
            {item.label}
          </span>
        )

        if (!item.timeRange) return <span key={`${item.label}-${index}`}>{pill}</span>

        return (
          <Tooltip key={`${item.label}-${index}`}>
            <TooltipTrigger asChild>{pill}</TooltipTrigger>
            <TooltipContent side="top">{item.timeRange}</TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

function HashDataNarrativeSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-hidden="true">
      <div className="flex flex-col gap-3">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="grid grid-cols-[100px_1fr] gap-3">
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className={`h-3 rounded ${index === 0 ? "w-4/5" : "w-3/5"}`} />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-16 rounded" />
        <div className="flex flex-wrap gap-1.5">
          <Skeleton className="h-6 w-24 rounded-lg" />
          <Skeleton className="h-6 w-28 rounded-lg" />
          <Skeleton className="h-6 w-20 rounded-lg" />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-16 rounded" />
        <div className="flex flex-wrap gap-1.5">
          <Skeleton className="h-6 w-14 rounded-lg" />
          <Skeleton className="h-6 w-20 rounded-lg" />
          <Skeleton className="h-6 w-24 rounded-lg" />
          <Skeleton className="h-6 w-16 rounded-lg" />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-24 rounded" />
        <Skeleton className="h-16 w-full rounded-md" />
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-24 rounded" />
        <Skeleton className="h-16 w-full rounded-md" />
      </div>
    </div>
  )
}

export function DrilldownCreativeInfoTab({
  model,
  narrative,
  rank,
  isVideo,
  isHashDataLoading = false,
  onSequenceSeek,
}: DrilldownCreativeInfoTabProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-5 px-6 py-5" aria-busy={isHashDataLoading}>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            {model.logoUrl ? (
              <img
                src={model.logoUrl}
                alt={model.brandName}
                className="size-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span
                className="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold uppercase text-white"
                style={{ backgroundColor: avatarColor(model.competitor) }}
              >
                {model.brandName.charAt(0)}
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent side="top">{model.brandName}</TooltipContent>
        </Tooltip>
        <p className="text-sm font-medium text-foreground">{model.brandName}</p>
      </div>

      <DrilldownCreativeMetricsGrid model={model} rank={rank} isRankLoading={isHashDataLoading} />

      {isHashDataLoading ? (
        <>
          <p className="sr-only">{t("drilldown.drawer.loadingCreative", "Loading creative…")}</p>
          <HashDataNarrativeSkeleton />
        </>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {TEXT_NARRATIVE_FIELDS.map((field) => (
              <NarrativeTextRow
                key={field}
                label={t(NARRATIVE_LABEL_KEYS[field], NARRATIVE_DEFAULTS[field])}
                value={narrative[field]}
              />
            ))}
          </div>

          <div className="flex flex-col">
            <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
              {t(NARRATIVE_LABEL_KEYS.concepts, NARRATIVE_DEFAULTS.concepts)}
            </p>
            <ConceptPills concepts={narrative.concepts} />
          </div>

          <div className="flex flex-col">
            <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
              {t(NARRATIVE_LABEL_KEYS.sequence, NARRATIVE_DEFAULTS.sequence)}
            </p>
            <SequencePills sequence={narrative.sequence} isVideo={isVideo} onSequenceSeek={onSequenceSeek} />
          </div>

          {DRAWER_NARRATIVE_FIELDS.filter((field) => field === "verbalHooks" || field === "textOverlay").map(
            (field) => (
              <div key={field} className="flex flex-col">
                <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">
                  {t(NARRATIVE_LABEL_KEYS[field], NARRATIVE_DEFAULTS[field])}
                </p>
                <p className="pt-2 text-[13px] leading-[21px] tracking-[-0.0762px] text-foreground/90">
                  {narrative[field]}
                </p>
              </div>
            ),
          )}
        </>
      )}
    </div>
  )
}
